import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { Calculator, Download, FileText, RefreshCw, Plus, Trash2, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, DollarSign, Settings2, X, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { getDLToken, ensureDLToken } from "@/lib/dlToken";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const money = (n) => `$${(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * AdminAccounting — monthly Profit & Loss report for the CPA.
 *
 * Revenue    = every invoice sent in the month (accrual basis)
 * Driver Pay = one row per route the driver worked that month
 *               (source: route.driver_pay || DEFAULT_DRIVER_PAY_PNL)
 * Manual     = free-form CPA-driven rows (rent, insurance, fuel, etc.)
 *
 * Export: CSV (QuickBooks / Xero) and printer-friendly PDF.
 */
export default function AdminAccounting() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [pnl, setPnl] = useState(null);
  const [ytd, setYtd] = useState(null);
  const [busy, setBusy] = useState(false);

  const [expDate, setExpDate] = useState("");
  const [expCategory, setExpCategory] = useState("");
  const [expDescription, setExpDescription] = useState("");
  const [expAmount, setExpAmount] = useState("");
  const [addBusy, setAddBusy] = useState(false);

  // Pinned expense categories — CPA can add/remove via the settings drawer.
  // First load seeds the default set (Rent, Fuel, Insurance, Marketing,
  // Office, Others) server-side so we always have chips on a fresh install.
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const loadCategories = async () => {
    try {
      const r = await api.get("/admin/accounting/categories");
      setCategories(r.data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("categories load failed", e);
    }
  };
  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    try {
      const r = await api.post("/admin/accounting/categories", { name });
      setCategories((prev) => [...prev, r.data]);
      setNewCat("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const removeCategory = async (id) => {
    try {
      await api.delete(`/admin/accounting/categories/${id}`);
      setCategories((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  // Inline rename — one category can be in edit mode at a time so keystrokes
  // don't accidentally propagate. Save is committed on Enter or ✓ click; Esc
  // cancels without touching the server.
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const startEdit = (c) => { setEditingId(c.id); setEditingName(c.name); };
  const cancelEdit = () => { setEditingId(null); setEditingName(""); };
  const saveEdit = async () => {
    const name = editingName.trim();
    const id = editingId;
    if (!id || !name) { cancelEdit(); return; }
    try {
      await api.patch(`/admin/accounting/categories/${id}`, { name });
      setCategories((prev) => prev.map((c) => c.id === id ? { ...c, name } : c));
      cancelEdit();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => { loadCategories(); }, []);

  const loadMonth = async (y = year, m = month) => {
    setBusy(true);
    try {
      const r = await api.get("/admin/accounting/pnl", { params: { year: y, month: m } });
      setPnl(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const loadYtd = async (y = year) => {
    try {
      const r = await api.get("/admin/accounting/pnl/ytd", { params: { year: y } });
      setYtd(r.data);
    } catch (e) {
      // silently — YTD is decorative
      // eslint-disable-next-line no-console
      console.warn("YTD load failed", e);
    }
  };

  useEffect(() => { loadMonth(); loadYtd(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadMonth(year, month); /* eslint-disable-next-line */ }, [year, month]);
  useEffect(() => { loadYtd(year); /* eslint-disable-next-line */ }, [year]);

  const shiftMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const addExpense = async () => {
    if (!expDate || !expCategory.trim() || !Number(expAmount)) {
      toast.error("Date, category, and amount are required.");
      return;
    }
    setAddBusy(true);
    try {
      await api.post("/admin/accounting/expenses", {
        date: expDate,
        category: expCategory.trim(),
        description: expDescription.trim(),
        amount: Number(expAmount),
      });
      setExpDate(""); setExpCategory(""); setExpDescription(""); setExpAmount("");
      toast.success("Expense added");
      loadMonth();
      loadYtd();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAddBusy(false);
    }
  };

  const deleteExpense = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/admin/accounting/expenses/${id}`);
      loadMonth();
      loadYtd();
      toast.success("Expense deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadExport = async (fmt) => {
    try {
      await ensureDLToken();
      const t = encodeURIComponent(getDLToken());
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/accounting/pnl.${fmt}?year=${year}&month=${month}&auth=${t}`;
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`;
  const ytdBars = useMemo(() => {
    if (!ytd?.months) return [];
    return ytd.months.map((m) => ({
      name: MONTH_NAMES[m.month - 1].slice(0, 3),
      Revenue: m.revenue,
      Expenses: m.expenses,
      Net: m.net,
    }));
  }, [ytd]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="admin-accounting">
      {/* ---- Header ---- */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
            <Calculator className="h-3.5 w-3.5" /> Accounting · Accrual Basis
          </div>
          <h1 className="text-2xl md:text-3xl font-bold mt-1">Profit &amp; Loss</h1>
          <div className="text-sm text-muted-foreground">
            Revenue from invoices sent · Driver payments from TimeSheet · Free-form manual expenses.
          </div>
        </div>

        {/* Month picker */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => shiftMonth(-1)} data-testid="accounting-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-[130px]" data-testid="accounting-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((n, i) => <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-[100px]" data-testid="accounting-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 6 }, (_, i) => now.getFullYear() - 3 + i).map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="icon" className="rounded-full" onClick={() => shiftMonth(1)} data-testid="accounting-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => { loadMonth(); loadYtd(); }} data-testid="accounting-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <div className="w-px h-6 bg-border mx-1" />
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadExport("csv")} data-testid="accounting-export-csv">
            <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
          <Button size="sm" className="rounded-full" onClick={() => downloadExport("pdf")} data-testid="accounting-export-pdf">
            <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {/* ---- Top summary ---- */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Card className="p-4 rounded-xl border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20" data-testid="accounting-kpi-revenue">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5"><TrendingUp className="h-3 w-3" /> Revenue ({periodLabel})</div>
          <div className="text-2xl font-bold font-mono-num mt-1">{money(pnl?.revenue_total)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{pnl?.revenue?.length || 0} invoice(s)</div>
        </Card>
        <Card className="p-4 rounded-xl border-rose-300 bg-rose-50 dark:bg-rose-900/20" data-testid="accounting-kpi-expenses">
          <div className="text-[10px] uppercase tracking-widest text-rose-700 dark:text-rose-300 flex items-center gap-1.5"><TrendingDown className="h-3 w-3" /> Total Expenses</div>
          <div className="text-2xl font-bold font-mono-num mt-1">{money(pnl?.total_expenses)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Driver {money(pnl?.driver_costs_total)} · Manual {money(pnl?.manual_expenses_total)}
          </div>
        </Card>
        <Card className={`p-4 rounded-xl ${Number(pnl?.net) >= 0 ? "border-emerald-400 bg-emerald-100/60 dark:bg-emerald-900/30" : "border-rose-400 bg-rose-100/60 dark:bg-rose-900/30"}`} data-testid="accounting-kpi-net">
          <div className="text-[10px] uppercase tracking-widest flex items-center gap-1.5"><DollarSign className="h-3 w-3" /> Net {Number(pnl?.net) >= 0 ? "Profit" : "Loss"}</div>
          <div className={`text-2xl font-bold font-mono-num mt-1 ${Number(pnl?.net) >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>{money(pnl?.net)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">{periodLabel}</div>
        </Card>
        <Card className="p-4 rounded-xl bg-secondary/50" data-testid="accounting-kpi-ytd">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">YTD {year} — Net</div>
          <div className={`text-2xl font-bold font-mono-num mt-1 ${Number(ytd?.net_total) >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>{money(ytd?.net_total)}</div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Rev {money(ytd?.revenue_total)} · Exp {money(ytd?.expenses_total)}
          </div>
        </Card>
      </div>

      {/* ---- YTD bar chart ---- */}
      <Card className="p-4 rounded-xl">
        <div className="flex items-center justify-between mb-2">
          <div className="font-semibold text-sm">YTD {year} — Monthly summary</div>
          <div className="text-xs text-muted-foreground">Green bars = revenue · red = expenses · dot = net</div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ytdBars}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `$${Math.abs(v) >= 1000 ? (v / 1000).toFixed(1) + "k" : v}`} />
              <Tooltip formatter={(v) => money(v)} />
              <Bar dataKey="Revenue" fill="#16A34A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Expenses" fill="#DC2626" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Net" fill="#1E6FE0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* ---- Revenue section ---- */}
      <Card className="rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-emerald-50 dark:bg-emerald-900/20 border-b border-emerald-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm text-emerald-800 dark:text-emerald-200 uppercase tracking-wider">Revenue — Invoices Sent</div>
            <div className="text-[11px] text-muted-foreground">Accrual basis · one line per invoice sent in {periodLabel}</div>
          </div>
          <div className="font-mono-num text-lg font-semibold text-emerald-700 dark:text-emerald-300">{money(pnl?.revenue_total)}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="accounting-revenue-table">
            <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-24">Date</th>
                <th className="text-left px-3 py-2 font-semibold w-28">Invoice #</th>
                <th className="text-left px-3 py-2 font-semibold">Pharmacy</th>
                <th className="text-left px-3 py-2 font-semibold w-40">Period</th>
                <th className="text-center px-3 py-2 font-semibold w-20">Status</th>
                <th className="text-right px-3 py-2 font-semibold w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {pnl?.revenue?.length ? pnl.revenue.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono-num text-xs">{r.date}</td>
                  <td className="px-3 py-2 font-mono-num text-xs">{r.invoice_number}</td>
                  <td className="px-3 py-2">{r.pharmacy}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground font-mono-num">{r.period_from} → {r.period_to}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${r.payment_status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.payment_status}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold">{money(r.amount)}</td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="text-center py-6 text-muted-foreground text-sm">No invoices sent this month.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---- Driver payments section ---- */}
      <Card className="rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-rose-50 dark:bg-rose-900/20 border-b border-rose-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm text-rose-800 dark:text-rose-200 uppercase tracking-wider">Driver Payments (Weekly · TimeSheet)</div>
            <div className="text-[11px] text-muted-foreground">One line per driver per week (Mon → Sun) in {periodLabel}. Amounts sum the routes worked that week. Rate warning shown when any route used the default fallback.</div>
          </div>
          <div className="font-mono-num text-lg font-semibold text-rose-700 dark:text-rose-300">-{money(pnl?.driver_costs_total)}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="accounting-driver-table">
            <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-52">Week (Mon → Sun)</th>
                <th className="text-left px-3 py-2 font-semibold">Driver</th>
                <th className="text-center px-3 py-2 font-semibold w-20">Routes</th>
                <th className="text-center px-3 py-2 font-semibold w-16">Stops</th>
                <th className="text-right px-3 py-2 font-semibold w-28">Amount</th>
              </tr>
            </thead>
            <tbody>
              {pnl?.driver_weekly?.length ? pnl.driver_weekly.map((r) => (
                <tr key={`${r.driver_id || "u"}-${r.week_start}`} className="border-t border-border">
                  <td className="px-3 py-2 font-mono-num text-xs">{r.week_start} → {r.week_end}</td>
                  <td className="px-3 py-2">
                    {r.driver}
                    {r.any_default_rate && (
                      <span className="ml-1.5 text-[10px] uppercase text-amber-700" title="At least one route in this week used the default rate — not overridden on TimeSheet">⚠ default</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center font-mono-num">{r.routes}</td>
                  <td className="px-3 py-2 text-center font-mono-num">{r.stops}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold text-rose-700 dark:text-rose-300">-{money(r.amount)}</td>
                </tr>
              )) : (
                <tr><td colSpan={5} className="text-center py-6 text-muted-foreground text-sm">No driver activity this month.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---- Manual expenses section ---- */}
      <Card className="rounded-xl overflow-hidden">
        <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 flex items-center justify-between">
          <div>
            <div className="font-semibold text-sm text-amber-800 dark:text-amber-200 uppercase tracking-wider">Manual Expenses</div>
            <div className="text-[11px] text-muted-foreground">Free-form CPA-driven rows for rent, insurance, fuel, licences, etc.</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="font-mono-num text-lg font-semibold text-amber-700 dark:text-amber-300">-{money(pnl?.manual_expenses_total)}</div>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="rounded-full h-8 w-8" title="Manage categories" data-testid="accounting-cat-settings">
                  <Settings2 className="h-3.5 w-3.5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-md">
                <SheetHeader>
                  <SheetTitle>Expense categories</SheetTitle>
                  <SheetDescription>Pin the categories your CPA uses most. They appear as one-tap chips above the add-expense row.</SheetDescription>
                </SheetHeader>
                <div className="mt-4 space-y-3">
                  <div className="flex gap-2">
                    <Input
                      value={newCat}
                      onChange={(e) => setNewCat(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                      placeholder="New category (e.g. Software, Bank Fees)"
                      data-testid="accounting-cat-new-input"
                    />
                    <Button onClick={addCategory} disabled={!newCat.trim()} data-testid="accounting-cat-add">
                      <Plus className="h-4 w-4 mr-1" /> Add
                    </Button>
                  </div>
                  <div className="border rounded-lg divide-y" data-testid="accounting-cat-list">
                    {categories.length === 0 && (
                      <div className="p-4 text-sm text-muted-foreground text-center">No categories yet.</div>
                    )}
                    {categories.map((c) => (
                      <div key={c.id} className="flex items-center justify-between p-2.5 gap-2">
                        {editingId === c.id ? (
                          <>
                            <Input
                              value={editingName}
                              onChange={(e) => setEditingName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { e.preventDefault(); saveEdit(); }
                                else if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
                              }}
                              autoFocus
                              className="h-8 text-sm"
                              data-testid={`accounting-cat-edit-input-${c.id}`}
                            />
                            <Button
                              size="icon" variant="ghost"
                              className="h-7 w-7 text-emerald-700 hover:text-emerald-800"
                              onClick={saveEdit}
                              data-testid={`accounting-cat-save-${c.id}`}
                              title="Save"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon" variant="ghost"
                              className="h-7 w-7 text-muted-foreground"
                              onClick={cancelEdit}
                              data-testid={`accounting-cat-cancel-${c.id}`}
                              title="Cancel"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="text-sm font-medium flex-1">{c.name}</span>
                            <Button
                              size="icon" variant="ghost"
                              className="h-7 w-7 text-muted-foreground hover:text-primary"
                              onClick={() => startEdit(c)}
                              data-testid={`accounting-cat-edit-${c.id}`}
                              title="Rename category"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-rose-600"
                              onClick={() => removeCategory(c.id)}
                              data-testid={`accounting-cat-remove-${c.id}`}
                              title="Remove category"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted-foreground">Removing a category only hides it from the chip bar. Past expenses tagged with that name stay unchanged.</p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        {/* Category chip bar */}
        {categories.length > 0 && (
          <div className="px-3 pt-3 flex flex-wrap gap-1.5" data-testid="accounting-cat-chips">
            {categories.map((c) => {
              const active = expCategory.trim().toLowerCase() === c.name.toLowerCase();
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setExpCategory(c.name)}
                  className={`px-2.5 h-7 rounded-full text-xs font-semibold transition-colors border ${
                    active
                      ? "bg-amber-400 text-amber-950 border-amber-500"
                      : "bg-background text-muted-foreground border-border hover:bg-amber-100 hover:text-amber-900 hover:border-amber-300"
                  }`}
                  data-testid={`accounting-cat-chip-${c.name}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
        {/* Add-row bar */}
        <div className="p-3 bg-secondary/30 border-b border-border grid grid-cols-1 md:grid-cols-[130px_180px_1fr_130px_100px] gap-2 items-center">
          <Input type="date" value={expDate} onChange={(e) => setExpDate(e.target.value)} data-testid="accounting-exp-date" placeholder="Date" />
          <Input value={expCategory} onChange={(e) => setExpCategory(e.target.value)} placeholder="Category (Rent, Fuel…)" data-testid="accounting-exp-category" />
          <Input value={expDescription} onChange={(e) => setExpDescription(e.target.value)} placeholder="Description (optional)" data-testid="accounting-exp-description" />
          <Input type="number" step="0.01" min="0" value={expAmount} onChange={(e) => setExpAmount(e.target.value)} placeholder="Amount" data-testid="accounting-exp-amount" />
          <Button size="sm" className="rounded-full" onClick={addExpense} disabled={addBusy} data-testid="accounting-exp-add">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="accounting-manual-table">
            <thead className="bg-secondary/40 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-semibold w-24">Date</th>
                <th className="text-left px-3 py-2 font-semibold w-36">Category</th>
                <th className="text-left px-3 py-2 font-semibold">Description</th>
                <th className="text-right px-3 py-2 font-semibold w-28">Amount</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody>
              {pnl?.manual_expenses?.length ? pnl.manual_expenses.map((m) => (
                <tr key={m.id} className="border-t border-border" data-testid={`accounting-exp-row-${m.id}`}>
                  <td className="px-3 py-2 font-mono-num text-xs">{m.date}</td>
                  <td className="px-3 py-2 font-medium">{m.category}</td>
                  <td className="px-3 py-2 text-muted-foreground">{m.description}</td>
                  <td className="px-3 py-2 text-right font-mono-num font-semibold text-amber-700 dark:text-amber-300">-{money(m.amount)}</td>
                  <td className="px-1 py-2 text-center">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-rose-600" onClick={() => deleteExpense(m.id)} data-testid={`accounting-exp-delete-${m.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={5} className="text-center py-6 text-muted-foreground text-sm">No manual expenses recorded this month.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ---- Footer totals ---- */}
      <Card className="p-4 rounded-xl bg-primary/5 border-primary/20">
        <div className="flex items-center justify-between text-sm">
          <div className="font-semibold uppercase tracking-widest text-xs text-muted-foreground">Bottom line — {periodLabel}</div>
          <div className="flex items-center gap-6 font-mono-num text-base">
            <div><span className="text-muted-foreground">Revenue </span><span className="font-bold text-emerald-700 dark:text-emerald-300">{money(pnl?.revenue_total)}</span></div>
            <div><span className="text-muted-foreground">Expenses </span><span className="font-bold text-rose-700 dark:text-rose-300">-{money(pnl?.total_expenses)}</span></div>
            <div className="border-l border-border pl-6"><span className="text-muted-foreground">Net </span><span className={`font-extrabold text-lg ${Number(pnl?.net) >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`}>{money(pnl?.net)}</span></div>
          </div>
        </div>
      </Card>
    </div>
  );
}
